// ops/sculptBrush.ts
import { Vector3 } from 'three';
import { Vertex } from '../core/Vertex';
import { Halfedge } from '../core/Halfedge';

export type BrushMode = 'project' | 'relax';
export type FalloffKind = 'none' | 'linear' | 'smooth' | 'gauss';
export type BoundaryPolicy = 'lock' | 'flatten' | 'slide';
export type NormalKind = 'face' | 'pca';
export type DirInput = Vector3 | { x: number; y: number; z: number } | [number, number, number];

export interface SculptOpts {
  // Brush size: either 'area' (world radius) or 'rings' (approximate => area ≈ rings * avgEdgeLen)
  area?: number;                 // world-space radius
  rings?: number;                // k-rings (converted to world radius internally)

  direction?: DirInput;          // explicit plane normal (world-space). If present with planeOffset/planePoint → hard plane mode
  planeOffset?: number;          // d in plane equation n·x = d (world units)
  planePoint?: DirInput;         // alternatively specify a point through which the plane passes (d = n·planePoint)

  // Behavior
  mode?: BrushMode;              // 'project' (centers idempotent with strength=1) | 'relax'
  strength?: number;             // 0..1 step size
  falloff?: FalloffKind;         // spatial falloff
  sigma?: number;                // stddev for gauss falloff (world units)
  boundaryPolicy?: BoundaryPolicy; // 'slide'|'flatten'|'lock'
  normalKind?: NormalKind;       // 'face' or 'pca'

  // Target plane control (for 'project' and as relax reference)
  percentile?: number;           // 0..100 (50 = flatten/median; ~10 fill; ~90 scrape)
  percentileBias?: [number, number]; // map strength -> percentile (p = lerp(pLow,pHigh,strength))

  // Quality safety for low-poly areas
  clamp?: {
    enabled?: boolean;           // backtrack if move hurts quality
    minQuality?: number;         // 0..1
    maxIterations?: number;      // backtracking steps
  };
}

const TMP = {
  v1: new Vector3(),
  v2: new Vector3(),
  v3: new Vector3(),
  n0: new Vector3(),
  n1: new Vector3(),
  p: new Vector3(),
};


function normalizeDirection(dir?: DirInput): Vector3 | null {
  if (!dir) return null;
  let v: Vector3;
  if (dir instanceof Vector3) v = dir.clone();
  else if (Array.isArray(dir) && dir.length===3) v = new Vector3(dir[0],dir[1],dir[2]);
  else if (typeof dir==='object' && 'x' in dir && 'y' in dir && 'z' in dir) v = new Vector3(dir.x,dir.y,dir.z);
  else return null;
  if (v.lengthSq()<1e-16) return null;
  return v.normalize();
}

function toVec3(p?: DirInput): Vector3 | null {
  if (!p) return null;
  if (p instanceof Vector3) return p.clone();
  if (Array.isArray(p) && p.length===3) return new Vector3(p[0],p[1],p[2]);
  if (typeof p==='object' && 'x' in p && 'y' in p && 'z' in p) return new Vector3(p.x,p.y,p.z);
  return null;
}

function isBoundaryVertex(v: Vertex): boolean {
  const start = v.halfedge as Halfedge | null | undefined;
  if (!start) return true;
  let he: Halfedge | null | undefined = start;
  do {
    if (!he!.face || !he!.twin?.face) return true;
    he = he!.twin ? he!.twin.next : null;
  } while (he && he !== start);
  return false;
}

function* oneRing(v: Vertex): Iterable<Vertex> {
  yield* v.oneRing();
}

function avgEdgeLen(v: Vertex): number {
  let sum = 0, cnt = 0;
  for (const u of oneRing(v)) { sum += u.position.distanceTo(v.position); cnt++; }
  return cnt ? Math.max(1e-9, sum / cnt) : 1;
}

/** Dijkstra on edge lengths (geodesic world distance) clipped by 'radius'. */
function geodesicDistances(center: Vertex, radius: number): Map<Vertex, number> {
  const dist = new Map<Vertex, number>();
  dist.set(center, 0);

  const q: Vertex[] = [center];
  while (q.length) {
    // extract-min (small queues: array is fine)
    let bestI = 0, bestD = dist.get(q[0])!;
    for (let i = 1; i < q.length; i++) {
      const di = dist.get(q[i])!;
      if (di < bestD) { bestD = di; bestI = i; }
    }
    const v = q.splice(bestI, 1)[0];
    const dv = bestD;
    if (dv > radius) continue;

    for (const u of oneRing(v)) {
      const w = u.position.distanceTo(v.position);
      const nd = dv + w;
      const old = dist.get(u);
      if (old === undefined && nd <= radius + 1e-9) {
        dist.set(u, nd);
        q.push(u);
      } else if (old !== undefined && nd < old) {
        dist.set(u, nd);
        if (!q.includes(u)) q.push(u);
      }
    }
  }
  return dist;
}

function weightByWorld(dWorld: number, radius: number, falloff: FalloffKind, sigma?: number): number {
  if (radius <= 0 || falloff === 'none') return 1;
  const x = Math.min(1, Math.max(0, dWorld / radius));
  if (falloff === 'linear') return 1 - x;
  if (falloff === 'gauss') {
    const s = sigma && sigma > 0 ? sigma : radius / 2;
    const t = dWorld / Math.max(1e-9, s);
    return Math.exp(-0.5 * t * t);
  }
  // 'smooth' (smoothstep)
  const t = 1 - x;
  return t * t * (3 - 2 * t);
}

/** Unweighted centroid (fall-back). */
function robustCentroid(vertices: Vertex[]): Vector3 {
  const c = new Vector3();
  for (let i = 0; i < vertices.length; i++) c.add(vertices[i].position);
  return vertices.length ? c.multiplyScalar(1 / vertices.length) : c;
}

/** Weighted centroid over Vertex positions. */
function weightedCentroidVec3(verts: Vertex[], w: number[], out = new Vector3()) {
  out.set(0,0,0);
  let sum = 0;
  for (let i = 0; i < verts.length; i++) {
    const wi = Math.max(0, w[i] || 0);
    out.addScaledVector(verts[i].position, wi);
    sum += wi;
  }
  if (sum > 0) out.multiplyScalar(1 / sum);
  return out;
}

/** Face-weighted normal at v (sums area-weighted incident face normals). */
function faceWeightedNormal(v: Vertex): Vector3 {
  const n = new Vector3();
  const start = v.halfedge as Halfedge | null | undefined;
  if (!start) return new Vector3(0, 0, 1);
  let he: Halfedge | null | undefined = start;
  do {
    const f = he!.face;
    if (f) {
      const a = he!.prev.vertex.position;
      const b = he!.vertex.position;
      const c = he!.next.vertex.position;
      TMP.v1.subVectors(b, a);
      TMP.v2.subVectors(c, a);
      TMP.v3.crossVectors(TMP.v1, TMP.v2);
      n.add(TMP.v3); // area-weighted
    }
    he = he!.twin ? he!.twin.next : null;
  } while (he && he !== start);
  if (n.lengthSq() < 1e-16) n.set(0, 0, 1);
  return n.normalize();
}

/** Jacobi eigen-decomposition for 3x3 symmetric matrix; returns eigenvectors in columns of V and diag in A. */
function jacobiSym3(A: number[], V: number[], iters = 10) {
  // A = [a00,a01,a02, a01,a11,a12, a02,a12,a22] (upper triangle packed as 6 entries) – we’ll expand to full for clarity.
  // Expand to full 3x3:
  let a00 = A[0], a01 = A[1], a02 = A[2], a11 = A[3], a12 = A[4], a22 = A[5];
  // V = identity initially
  let v00 = 1, v01 = 0, v02 = 0,
      v10 = 0, v11 = 1, v12 = 0,
      v20 = 0, v21 = 0, v22 = 1;

  const rotate = (p: number, q: number) => {
    // pick apq, app, aqq
    let apq = 0, app = 0, aqq = 0;
    if (p === 0 && q === 1) { apq = a01; app = a00; aqq = a11; }
    if (p === 0 && q === 2) { apq = a02; app = a00; aqq = a22; }
    if (p === 1 && q === 2) { apq = a12; app = a11; aqq = a22; }
    if (Math.abs(apq) < 1e-15) return;

    const phi = 0.5 * Math.atan2(2 * apq, (aqq - app));
    const c = Math.cos(phi), s = Math.sin(phi);

    const apply = (a00_: number, a01_: number, a02_: number, a11_: number, a12_: number, a22_: number) => {
      a00 = a00_; a01 = a01_; a02 = a02_; a11 = a11_; a12 = a12_; a22 = a22_;
    };

    // rotate A in (p,q)
    if (p === 0 && q === 1) {
      const a00n = c*c*a00 - 2*s*c*a01 + s*s*a11;
      const a01n = s*c*(a00 - a11) + (c*c - s*s)*a01;
      const a11n = s*s*a00 + 2*s*c*a01 + c*c*a11;
      const a02n = c*a02 - s*a12;
      const a12n = s*a02 + c*a12;
      apply(a00n, a01n, a02n, a11n, a12n, a22);
      // rotate V columns (0,1)
      const v00n = c*v00 - s*v01, v01n = s*v00 + c*v01;
      const v10n = c*v10 - s*v11, v11n = s*v10 + c*v11;
      const v20n = c*v20 - s*v21, v21n = s*v20 + c*v21;
      v00 = v00n; v01 = v01n; v10 = v10n; v11 = v11n; v20 = v20n; v21 = v21n;
    } else if (p === 0 && q === 2) {
      const a00n = c*c*a00 - 2*s*c*a02 + s*s*a22;
      const a02n = s*c*(a00 - a22) + (c*c - s*s)*a02;
      const a22n = s*s*a00 + 2*s*c*a02 + c*c*a22;
      const a01n = c*a01 - s*a12;
      const a12n = s*a01 + c*a12;
      apply(a00n, a01n, a02n, a11, a12n, a22n);
      // rotate V columns (0,2)
      const v00n = c*v00 - s*v02, v02n = s*v00 + c*v02;
      const v10n = c*v10 - s*v12, v12n = s*v10 + c*v12;
      const v20n = c*v20 - s*v22, v22n = s*v20 + c*v22;
      v00 = v00n; v02 = v02n; v10 = v10n; v12 = v12n; v20 = v20n; v22 = v22n;
    } else { // (1,2)
      const a11n = c*c*a11 - 2*s*c*a12 + s*s*a22;
      const a12n = s*c*(a11 - a22) + (c*c - s*s)*a12;
      const a22n = s*s*a11 + 2*s*c*a12 + c*c*a22;
      const a01n = c*a01 - s*a02;
      const a02n = s*a01 + c*a02;
      apply(a00, a01n, a02n, a11n, a12n, a22n);
      // rotate V columns (1,2)
      const v01n = c*v01 - s*v02, v02n = s*v01 + c*v02;
      const v11n = c*v11 - s*v12, v12n = s*v11 + c*v12;
      const v21n = c*v21 - s*v22, v22n = s*v21 + c*v22;
      v01 = v01n; v02 = v02n; v11 = v11n; v12 = v12n; v21 = v21n; v22 = v22n;
    }
  }

  for (let it = 0; it < iters; it++) {
    // rotate the largest off-diagonal first
    let p = 0, q = 1, off = Math.abs(a01);
    if (Math.abs(a02) > off) { p = 0; q = 2; off = Math.abs(a02); }
    if (Math.abs(a12) > off) { p = 1; q = 2; off = Math.abs(a12); }
    if (off < 1e-12) break;
    rotate(p, q);
  }

  // pack back A (upper)
  A[0] = a00; A[1] = a01; A[2] = a02; A[3] = a11; A[4] = a12; A[5] = a22;
  // pack V column-major: [v00,v10,v20, v01,v11,v21, v02,v12,v22]
  V[0] = v00; V[1] = v10; V[2] = v20;
  V[3] = v01; V[4] = v11; V[5] = v21;
  V[6] = v02; V[7] = v12; V[8] = v22;
}

/** Weighted PCA plane normal (smallest eigenvector of covariance). */
function pcaPlaneNormalVerticesWeighted(points: Vertex[], w: number[], c: Vector3): Vector3 {
  let xx=0, xy=0, xz=0, yy=0, yz=0, zz=0, sum=0;
  for (let i=0;i<points.length;i++) {
    const wi = Math.max(0, w[i] || 0);
    TMP.v1.subVectors(points[i].position, c);
    const x = TMP.v1.x, y = TMP.v1.y, z = TMP.v1.z;
    xx += wi*x*x; xy += wi*x*y; xz += wi*x*z;
    yy += wi*y*y; yz += wi*y*z; zz += wi*z*z;
    sum += wi;
  }
  const inv = sum > 0 ? 1/sum : 1;
  const A = [xx*inv, xy*inv, xz*inv, yy*inv, yz*inv, zz*inv];
  const V = new Array(9).fill(0);
  jacobiSym3(A, V, 12);
  // eigenvalues (on diag of A) are [a00,a11,a22] after rotations; choose smallest => its column in V
  const evals = [A[0], A[3], A[5]];
  let k = 0; if (evals[1] < evals[k]) k = 1; if (evals[2] < evals[k]) k = 2;
  const n = new Vector3(V[0+3*k], V[1+3*k], V[2+3*k]);
  if (!isFinite(n.x+n.y+n.z) || n.lengthSq() < 1e-16) n.set(0,0,1);
  return n.normalize();
}

/** Boundary tangent (averaged, projected into local tangent plane) */
function boundaryTangent(v: Vertex): Vector3 | null {
  const n = faceWeightedNormal(v);
  const acc = new Vector3();
  let has = false;

  const start = v.halfedge as Halfedge | null | undefined;
  if (!start) return null;
  let he: Halfedge | null | undefined = start;
  do {
    const u = he!.twin?.vertex;
    if (u && isBoundaryVertex(u)) {
      const d = TMP.v1.subVectors(u.position, v.position);
      const dn = d.dot(n);
      d.addScaledVector(n, -dn); // project to tangent plane
      if (d.lengthSq() > 1e-14) {
        d.normalize();
        acc.add(d);
        has = true;
      }
    }
    he = he!.twin ? he!.twin.next : null;
  } while (he && he !== start);

  if (!has || acc.lengthSq() < 1e-12) return null;
  return acc.normalize();
}

function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const clamped = Math.min(100, Math.max(0, p));
  const arr = values.slice().sort((a, b) => a - b);
  if (clamped <= 0) return arr[0];
  if (clamped >= 100) return arr[arr.length - 1];
  const k = (clamped / 100) * (arr.length - 1);
  const i = Math.floor(k);
  const f = k - i;
  return i + 1 < arr.length ? arr[i] * (1 - f) + arr[i + 1] * f : arr[i];
}

function triQuality(a: Vector3, b: Vector3, c: Vector3): number {
  const ab = a.distanceTo(b), bc = b.distanceTo(c), ca = c.distanceTo(a);
  const s = 0.5 * (ab + bc + ca);
  const area = Math.max(0, s * (s - ab) * (s - bc) * (s - ca)) ** 0.5;
  const denom = ab * ab + bc * bc + ca * ca;
  return denom > 0 ? (4 * Math.sqrt(3) * area) / denom : 0;
}

function* incidentFaces(v: Vertex): Iterable<[Vector3, Vector3, Vector3]> {
  const start = v.halfedge as Halfedge | null | undefined;
  if (!start) return;
  let he: Halfedge | null | undefined = start;
  do {
    if (he!.face) {
      yield [he!.prev.vertex.position, he!.vertex.position, he!.next.vertex.position];
    }
    he = he!.twin ? he!.twin.next : null;
  } while (he && he !== start);
}

/** Backtrack along the move until incident triangles keep orientation and quality >= minQ. */
function clampMoveByQuality(v: Vertex, target: Vector3, minQ: number, maxIter: number): Vector3 {
  const x0 = v.position.clone();
  let t = 1;
  for (let it = 0; it < maxIter; it++) {
    TMP.p.copy(x0).add(TMP.v1.subVectors(target, x0).multiplyScalar(t));
    let ok = true;
    for (const [aa, bb, cc] of incidentFaces(v)) {
      const qa = triQuality(aa, TMP.p, cc);
      // preserve signed area/orientation on one fan triangle as a cheap check
      TMP.v1.subVectors(bb, aa); TMP.v2.subVectors(cc, aa); TMP.n0.crossVectors(TMP.v1, TMP.v2);
      TMP.v1.subVectors(TMP.p, aa); TMP.v2.subVectors(cc, aa); TMP.n1.crossVectors(TMP.v1, TMP.v2);
      if (TMP.n0.dot(TMP.n1) <= 0 || qa < minQ) { ok = false; break; }
    }
    if (ok) return TMP.p.clone();
    t *= 0.5;
  }
  return x0;
}

export function sculptVertex(center: Vertex, opts: SculptOpts = {}): boolean {
  const mode = opts.mode ?? 'project';
  const falloff = opts.falloff ?? 'smooth';
  const boundaryPolicy = opts.boundaryPolicy ?? 'slide';
  const strength = Math.min(1, Math.max(0, opts.strength ?? 1));

  // radius (world)
  const el = avgEdgeLen(center);
  const radius = (opts.area && opts.area > 0) ? opts.area : Math.max(1e-9, (opts.rings ?? 1) * el);
  if (boundaryPolicy === 'lock' && isBoundaryVertex(center)) return false;

  // geodesic neighborhood within radius
  const dist = geodesicDistances(center, radius);
  const brushVerts: Vertex[] = [];
  const wSpace: number[] = [];
  for (const [u, dWorld] of dist) {
    const w = weightByWorld(dWorld, radius, falloff, opts.sigma);
    if (w > 1e-6) { brushVerts.push(u); wSpace.push(w); }
  }
  if (brushVerts.length < 3) return false;

  // ---------- HARD PLANE MODE ----------
  const dirN = normalizeDirection(opts.direction);
  const planePointOpt = toVec3(opts.planePoint);
  const hardPlane = !!dirN && (opts.planeOffset !== undefined || planePointOpt);
  
  // We'll need a centroid if planePoint is not given
  // (reuse your existing weightedCentroidVec3 and wSpace/brushVerts)
  let P0: Vector3; // plane point
  let n: Vector3;  // plane normal
  
  if (hardPlane) {
    n = dirN!;
    // anchor point: explicit planePoint, otherwise weighted centroid of the brush
    const anchor = planePointOpt ?? center.position; // <- changed from weighted centroid
    const offset = opts.planeOffset ?? 0;
    P0 = anchor.clone().addScaledVector(n, offset);
  } else {
    // original percentile-based plane
    const c = weightedCentroidVec3(brushVerts, wSpace, new Vector3());
    n = (opts.normalKind === 'pca')
      ? pcaPlaneNormalVerticesWeighted(brushVerts, wSpace, c)
      : faceWeightedNormal(center);
  
    const hs: number[] = [];
    for (let i = 0; i < brushVerts.length; i++) {
      TMP.v1.subVectors(brushVerts[i].position, c);
      hs.push(n.dot(TMP.v1));
    }
    const strength = Math.min(1, Math.max(0, opts.strength ?? 1));
    const pctl =
      opts.percentile != null ? opts.percentile :
      (opts.percentileBias ? (opts.percentileBias[0]*(1-strength) + opts.percentileBias[1]*strength) : 50);
  
    const hStar = percentile(hs, pctl);
    P0 = new Vector3().copy(n).multiplyScalar(hStar).add(c);
  }

  // apply displacement (project or relax). For “perfect flatten”, set strength=1 and falloff='none'
  let changed = false;
  for (let i = 0; i < brushVerts.length; i++) {
    const u = brushVerts[i];
    if (boundaryPolicy === 'lock' && isBoundaryVertex(u)) continue;

    const w = wSpace[i];
    if (w <= 1e-6) continue;

    const x = u.position;
    TMP.v1.subVectors(x, P0);
    const delta = n.dot(TMP.v1);
    if (Math.abs(delta) < 1e-12) continue;

    let target: Vector3;
    if (mode === 'project') {
      // full projection onto plane, optionally blended by strength*falloff
      TMP.v2.copy(n).multiplyScalar(delta); // normal component
      TMP.v3.copy(x).sub(TMP.v2);          // projected position
      const su = strength * w;
      target = (su >= 1 - 1e-6) ? TMP.v3.clone() : x.clone().lerp(TMP.v3, su);
    } else {
      // relax: step towards plane along normal
      const su = strength * w;
      TMP.v2.copy(n).multiplyScalar(delta * su);
      TMP.v3.copy(x).sub(TMP.v2);
      target = TMP.v3.clone();
    }

    if (boundaryPolicy === 'slide' && isBoundaryVertex(u)) {
      const disp = TMP.v2.copy(target).sub(x);
      const t = boundaryTangent(u);
      if (t) {
        const mag = disp.dot(t);
        target.copy(x).addScaledVector(t, mag);
      } else {
        const nLocal = faceWeightedNormal(u);
        const dn = disp.dot(nLocal);
        target.copy(x).addScaledVector(disp.addScaledVector(nLocal, -dn), 1);
      }
    }

    if (opts.clamp?.enabled) {
      const minQ = opts.clamp.minQuality ?? 0.15;
      const maxI = opts.clamp.maxIterations ?? 10;
      target = clampMoveByQuality(u, target, minQ, maxI);
    }

    if (u.position.distanceToSquared(target) > 1e-18) {
      u.position.copy(target);
      changed = true;
    }
  }
  return changed;
}

export default function sculpt(centers: Vertex[], opts: SculptOpts = {}): number {
  let changed = 0;
  for (const v of centers) {
    const direction = opts.direction as Vector3
    direction.sub(v.position);
    opts.direction = direction.normalize();
    if (sculptVertex(v, opts)) changed++;
  }
  return changed;
}